import { useState } from 'react';
import { sendInvoiceVoidNotice } from '../services/invoiceService';

/**
 * Drives the "notify the supplier?" step that follows a manual void.
 *
 * Voiding no longer emails on its own, so every manual void path opens this and lets the admin
 * confirm, edit or add addresses — or decline, which skips only the email. Shared by the three
 * places an admin can void an invoice so they behave identically.
 *
 * Usage: pass the whole voidInvoice() response to openVoidNotice, and spread voidNoticeProps
 * onto <VoidNoticeModal />.
 */
export const useVoidNotice = () => {
  const [target, setTarget] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const openVoidNotice = (voidResponse) => {
    const data = voidResponse?.data;
    if (!data?.id) return;
    setResult(null);
    setSending(false);
    setTarget({
      invoiceId: data.id,
      invoiceNumber: data.invoiceNumber || null,
      supplierName: data.supplierName || null,
      suggestedRecipients: data.suggestedRecipients || [],
      recipientReason: data.recipientReason || null,
    });
  };

  const closeVoidNotice = () => {
    setTarget(null);
    setResult(null);
    setSending(false);
  };

  const sendVoidNotice = async (recipients) => {
    if (!target?.invoiceId) return;
    setSending(true);
    try {
      setResult(await sendInvoiceVoidNotice(target.invoiceId, recipients));
    } catch (error) {
      // Shown in the dialog rather than thrown — the void already succeeded, only the email failed.
      setResult({ sent: false, message: error.message || 'Could not send the notification.' });
    } finally {
      setSending(false);
    }
  };

  return {
    openVoidNotice,
    voidNoticeProps: {
      isOpen: !!target,
      invoiceNumber: target?.invoiceNumber,
      supplierName: target?.supplierName,
      suggestedRecipients: target?.suggestedRecipients,
      recipientReason: target?.recipientReason,
      sending,
      result,
      onSend: sendVoidNotice,
      onSkip: closeVoidNotice,
    },
  };
};
